import { useState } from "react";
import { useApp } from "@/store/AppStore";
import Modal from "@/components/base/Modal";
import { ghostButtonClass } from "@/components/base/fieldStyles";
import { formatCOP, formatNumber } from "@/utils/format";
import type { Wallet } from "@/types";

const ZERO_WALLETS: Record<Wallet, number> = {
  Bancolombia: 0,
  Daviplata: 0,
  Nequi: 0,
  "Caja Social": 0,
  Efectivo: 0,
};

// Borra la información contable ingresada a mano (gastos y saldos por billetera).
// No toca catálogo, clientes, inventario ni ventas.
const AccountingResetPanel = () => {
  const { data, deleteExpense, updateSettings } = useApp();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [feedback, setFeedback] = useState("");

  const expensesTotal = data.expenses.reduce((acc, e) => acc + e.amount, 0);
  const walletsTotal = Object.values(data.settings.walletBalances).reduce(
    (acc, v) => acc + (v ?? 0),
    0
  );

  const run = async () => {
    setWorking(true);
    await Promise.all(data.expenses.map((e) => deleteExpense(e.id)));
    await updateSettings({ walletBalances: { ...ZERO_WALLETS } });
    setWorking(false);
    setConfirmOpen(false);
    setFeedback("Información contable borrada: gastos vaciados y saldos en cero.");
    setTimeout(() => setFeedback(""), 3200);
  };

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-md bg-secondary-500/25 text-secondary-200 flex items-center justify-center">
          <i className="ri-calculator-line" />
        </span>
        <h3 className="editable-title text-base font-bold">Borrar información contable</h3>
      </div>
      <p className="text-[11px] text-foreground-500 mb-4">
        Vacía lo que has registrado en contabilidad: <strong>gastos</strong> y{" "}
        <strong>saldos por billetera</strong>. No toca catálogo, clientes, inventario, ventas ni
        cartera. Útil para dejar todo en cero y volver a empezar las pruebas.
      </p>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500 flex items-center gap-1">
            <i className="ri-wallet-3-line" /> Gastos registrados
          </p>
          <p className="text-sm font-bold text-foreground-950">
            {formatNumber(data.expenses.length)} · {formatCOP(expensesTotal)}
          </p>
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500 flex items-center gap-1">
            <i className="ri-coins-line" /> Saldo total en billeteras
          </p>
          <p className="text-sm font-bold text-foreground-950">{formatCOP(walletsTotal)}</p>
        </div>
      </div>

      <button onClick={() => setConfirmOpen(true)} className={ghostButtonClass}>
        <i className="ri-eraser-line" /> Borrar información contable
      </button>

      {feedback && (
        <p className="text-[11px] text-accent-200 mt-3 flex items-center gap-1.5 animate-fade-up">
          <i className="ri-checkbox-circle-line" /> {feedback}
        </p>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Borrar información contable"
        icon="ri-alert-line"
        size="sm"
        footer={
          <>
            <button onClick={() => setConfirmOpen(false)} className={ghostButtonClass}>
              Cancelar
            </button>
            <button
              onClick={run}
              disabled={working}
              className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-eraser-line" /> {working ? "Borrando..." : "Sí, borrar"}
            </button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          Se eliminarán <strong className="text-foreground-950">{data.expenses.length} gasto(s)</strong>{" "}
          y todos los <strong className="text-foreground-950">saldos por billetera</strong> quedarán
          en cero. Esta acción no se puede deshacer (guarda un respaldo JSON antes si quieres poder
          volver).
        </p>
      </Modal>
    </div>
  );
};

export default AccountingResetPanel;